import type { AxiosError, AxiosResponse } from 'axios';
import { ReactElement, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { LogIn as LogInIcon } from 'react-feather';
import { useForm } from 'react-hook-form';
import { Button, FormControl, HStack, Text, Box } from '@chakra-ui/react';
import { colors } from '@/styles/theme/foundations/colors';
import LoginLayout from '@/pages/login/layout';
import { NextPageWithLayout } from '@/pages/_app';
import { FormErrorMessageWithIcon, PasswordInput, UsernameInput } from '@/components/login/FormElements';
import { authControllerLogin } from '@/generated/auth/auth';
import type { AuthEmailLoginDto } from '@/generated/nabisNYWebAPI.schemas';
import { useAuthToken } from '@/modules/Auth';
import { GoogleOneTapLogin } from '@/components/login/google-one-tap';
import { GoogleOauthLogin } from '@/components/login/google-oauth';
import { AuthLoginDto } from '@/generated/nabisNYWebAPI.schemas';
import { auth, home } from '@/modules/link';
import { isNabisUser } from '@/util/is-nabis-user';
import { formContaier } from '@/components/login/styles';
import { LogoLogin } from '../../components/login/logo-login';

const Login: NextPageWithLayout = () => {
  const router = useRouter();
  const { setToken, setRefreshToken, redirectUrl } = useAuthToken();
  const { register, watch, handleSubmit, setError, formState } = useForm<AuthEmailLoginDto>();
  const [showGoogleLogin, setShowGoogleLogin] = useState(false);

  // watch email to trigger google one click sign-in
  const email = watch('email');
  useEffect(() => {
    const shouldShow = email ? isNabisUser(email) : false;
    if (shouldShow !== showGoogleLogin) {
      setShowGoogleLogin(shouldShow);
    }
  }, [email, showGoogleLogin]);

  /**
   * Handle success from an EmailAuth success and google one tap success
   * @param authResult
   */
  const onAuthSuccess = async (authResult: AxiosResponse<AuthLoginDto>) => {
    setToken(authResult.data.token);
    setRefreshToken(authResult.data.refreshToken);

    if (redirectUrl) {
      window.location.replace(redirectUrl);
    } else {
      await router.push(home);
    }
  };

  /**
   * Handle errors from an EmailAuth success and google one tap success
   * @param e
   */
  const onAuthError = (e: AxiosError<{ errors: Record<string, string> }>) => {
    const errors = e?.response?.data.errors || {};
    Object.keys(errors).forEach((key) => {
      setError(key as keyof AuthEmailLoginDto, { message: errors[key] });
    });
    console.error(e);
  };

  const onSubmit = ({ email, password }: AuthEmailLoginDto) => {
    authControllerLogin({ email, password }).then(onAuthSuccess).catch(onAuthError);
  };

  return (
    <Box>
      <form
        role="login-form"
        onSubmit={(event) => void handleSubmit(onSubmit)(event)}
        style={{ ...formContaier, marginBottom: '1rem' }}
      >
        <LogoLogin />
        <Text fontSize={20} fontWeight={600} color={colors.text.base.dark}>
          Welcome.
        </Text>
        <Text color={colors.text.secondary.light}>Please sign in to your Nabis account.</Text>
        <FormControl isInvalid={!!Object.keys(formState.errors).length}>
          <UsernameInput register={register} formState={formState} />
          {!showGoogleLogin && <PasswordInput register={register} formState={formState} />}
          <FormErrorMessageWithIcon message={formState?.errors?.root?.message} />
          <FormErrorMessageWithIcon message={formState?.errors?.email?.message} />
          <FormErrorMessageWithIcon message={formState?.errors?.password?.message} />
        </FormControl>
        <HStack spacing="var(--chakra-space-6)">
          {!showGoogleLogin && (
            <>
              <Button type="submit" color={colors.text.black} rightIcon={<LogInIcon width={'1rem'} height={'1rem'} />}>
                Sign in
              </Button>
              <Button
                type={'button'}
                color={colors.text.base.dark}
                variant={'link'}
                onClick={() => void router.push(auth.forgotPassword)}
              >
                Forgot password?
              </Button>
            </>
          )}
          {showGoogleLogin && (
            <Text color={colors.text.secondary.light}>Nabis employees must sign in with Google.</Text>
          )}
        </HStack>
      </form>

      {showGoogleLogin && (
        <GoogleOauthLogin onAuthSuccess={(args) => void onAuthSuccess(args)} onAuthError={onAuthError} />
      )}
      {showGoogleLogin && (
        <GoogleOneTapLogin onAuthSuccess={(args) => void onAuthSuccess(args)} onAuthError={onAuthError} />
      )}
    </Box>
  );
};

Login.getLayout = (page: ReactElement) => <LoginLayout>{page}</LoginLayout>;

export default Login;
